/**
 * IndexedDB-backed queue for mutations made while offline: course/podcast
 * highlights and project markdown file puts (ported from xdface/library's
 * highlight-queue, extended for the vault's models).
 *
 * Ops are replayed in insertion order by processQueue(), which receives its
 * service calls by dependency injection (avoids circular imports with the
 * services layer). Window events keep the UI in sync:
 *  - `vault:hl-queued`    after every enqueue* (pending count changed)
 *  - `vault:hl-confirmed` when a queued create returns the saved highlight
 *  - `vault:project-conflict` when a project-file-put is blocked by 409
 *  - `vault:queue-drained` after every processQueue() run
 */

import { clearDraft } from './editor-drafts.js';
import { auth } from '../services/auth.js';
import type { HighlightResponse } from '../services/courses.js';

const DB_NAME = 'vault-offline-queue';
const DB_VERSION = 1;
const STORE = 'hl-ops';
// Snapshots from independent editor sessions must never replace each other.
const ORIGIN_ID = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

/** Temp ids used by optimistic UI before the server confirms a create. */
export const OPTIMISTIC_ID_PREFIX = 'optimistic-';

// ── Types ────────────────────────────────────────────────────────────────────

export type QueuedOp = {
  ownerId?: string;
  blocked?: boolean;
  inFlight?: boolean;
  originId?: string;
} & (
  | { id?: number; type: 'course-create'; data: unknown; tempId: string; queuedAt: string }
  | { id?: number; type: 'course-delete'; hlId: string; queuedAt: string }
  | { id?: number; type: 'course-update'; hlId: string; patch: unknown; queuedAt: string }
  | {
      id?: number;
      type: 'podcast-replace';
      podcastId: string;
      highlights: unknown[];
      thoughts?: unknown[];
      queuedAt: string;
    }
  | {
      id?: number;
      type: 'project-file-put';
      projectId: string;
      path: string;
      content: string;
      expectedVersion: number;
      queuedAt: string;
    }
);

/** Payload convention for course-create ops (what the drain handler needs). */
export interface CourseCreateQueueData {
  courseId: string;
  lessonNum: number;
  input: {
    text: string;
    color: HighlightResponse['color'];
    quote?: string;
    startOffset?: number;
    endOffset?: number;
    prefix?: string;
    suffix?: string;
  };
}

/**
 * Composite reference for course highlight ops: `${courseId}/${lessonNum}/${hlId}`.
 * The highlight REST routes are nested under the course+lesson, so the bare
 * highlight id alone is not enough to replay a delete/update offline.
 */
export function courseHighlightRef(courseId: string, lessonNum: number, hlId: string): string {
  return `${courseId}/${lessonNum}/${hlId}`;
}

export function parseCourseHighlightRef(
  ref: string,
): { courseId: string; lessonNum: number; hlId: string } | null {
  const [courseId, lessonNumStr, hlId] = ref.split('/');
  const lessonNum = Number(lessonNumStr);
  if (!courseId || !hlId || !Number.isInteger(lessonNum)) return null;
  return { courseId, lessonNum, hlId };
}

/** Payload convention for project-file-put ops (what the drain handler needs). */
export interface ProjectFilePutQueueData {
  projectId: string;
  path: string;
  content: string;
  expectedVersion: number;
}

export interface QueueHandlers {
  /** Resolves the saved highlight (or null when nothing was created). */
  courseCreate(data: unknown, tempId: string): Promise<unknown | null>;
  courseDelete(hlId: string): Promise<void>;
  courseUpdate(hlId: string, patch: unknown): Promise<void>;
  podcastReplace(podcastId: string, highlights: unknown[], thoughts?: unknown[]): Promise<void>;
  projectFilePut(data: ProjectFilePutQueueData): Promise<void>;
}

// ── DB helpers ────────────────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbAll(store: IDBObjectStore): Promise<QueuedOp[]> {
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as QueuedOp[]);
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(store: IDBObjectStore, key: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function transactionComplete(tx: IDBTransaction): Promise<void> {
  const done = new Promise<void>((resolve, reject) => {
    tx.addEventListener('complete', () => resolve(), { once: true });
    tx.addEventListener(
      'abort',
      () => reject(tx.error ?? new Error('La transacción local fue cancelada')),
      { once: true },
    );
  });
  // A request can fail before its transaction aborts; keep that later rejection observed.
  void done.catch(() => {});
  return done;
}

async function idbAdd(store: IDBObjectStore, value: QueuedOp, ownerId: string): Promise<void> {
  const done = transactionComplete(store.transaction);
  await new Promise<void>((resolve, reject) => {
    const req = store.add({ ...value, ownerId });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  await done;
}

function notifyQueued(): void {
  window.dispatchEvent(new CustomEvent('vault:hl-queued'));
}

function currentOwner(expectedOwner?: string | null): string {
  const owner = auth.getUserId();
  if (!owner || (expectedOwner !== undefined && owner !== expectedOwner))
    throw new Error('No se pudo identificar la cuenta para guardar cambios offline');
  return owner;
}

// ── Enqueue API ───────────────────────────────────────────────────────────────

export async function enqueueCourseCreate(
  data: unknown,
  tempId: string,
  expectedOwner?: string | null,
): Promise<void> {
  const ownerId = currentOwner(expectedOwner);
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  await idbAdd(
    tx.objectStore(STORE),
    {
      type: 'course-create',
      data,
      tempId,
      queuedAt: new Date().toISOString(),
    },
    ownerId,
  );
  db.close();
  notifyQueued();
}

/**
 * Deleting a highlight that was created offline and never synced cancels the
 * pending create instead of queueing a delete: temp ids are not valid server
 * ids, so the delete would be rejected and retried forever (poisoned op).
 */
export async function enqueueCourseDelete(
  hlIdOrTempId: string,
  expectedOwner?: string | null,
): Promise<void> {
  const ownerId = currentOwner(expectedOwner);
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const committed = transactionComplete(tx);

  if (hlIdOrTempId.startsWith(OPTIMISTIC_ID_PREFIX)) {
    const ops = await idbAll(store);
    const pendingCreate = ops.find(
      (op) => op.ownerId === ownerId && op.type === 'course-create' && op.tempId === hlIdOrTempId,
    );
    if (pendingCreate?.inFlight) {
      await committed;
      db.close();
      throw new Error('El subrayado se está sincronizando; intentá eliminarlo nuevamente.');
    }
    if (pendingCreate?.id !== undefined) {
      await idbDelete(store, pendingCreate.id);
    }
    await committed;
    db.close();
    notifyQueued();
    return;
  }

  await idbAdd(
    store,
    {
      type: 'course-delete',
      hlId: hlIdOrTempId,
      queuedAt: new Date().toISOString(),
    },
    ownerId,
  );
  db.close();
  notifyQueued();
}

export async function enqueueCourseUpdate(
  hlId: string,
  patch: unknown,
  expectedOwner?: string | null,
): Promise<void> {
  const ownerId = currentOwner(expectedOwner);
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  await idbAdd(
    tx.objectStore(STORE),
    {
      type: 'course-update',
      hlId,
      patch,
      queuedAt: new Date().toISOString(),
    },
    ownerId,
  );
  db.close();
  notifyQueued();
}

/**
 * Podcast highlights are stored as a whole array per podcast, so only the
 * latest snapshot matters: any pending replace for the same podcast is
 * removed before queueing the new one.
 */
export async function enqueuePodcastReplace(
  podcastId: string,
  highlights: unknown[],
  thoughts?: unknown[],
  expectedOwner?: string | null,
): Promise<void> {
  const ownerId = currentOwner(expectedOwner);
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);

  const ops = await idbAll(store);
  for (const op of ops) {
    if (
      op.ownerId === ownerId &&
      op.originId === ORIGIN_ID &&
      !op.blocked &&
      !op.inFlight &&
      op.type === 'podcast-replace' &&
      op.podcastId === podcastId &&
      op.id !== undefined
    ) {
      await idbDelete(store, op.id);
    }
  }

  await idbAdd(
    store,
    {
      type: 'podcast-replace',
      originId: ORIGIN_ID,
      podcastId,
      highlights,
      ...(thoughts ? { thoughts } : {}),
      queuedAt: new Date().toISOString(),
    },
    ownerId,
  );
  db.close();
  notifyQueued();
}

/**
 * Markdown autosaves are whole-file snapshots: only the latest pending put per
 * project+path matters, so any older pending put for the same file is removed
 * before queueing the new one. Conflicts stay blocked for explicit recovery.
 */
export async function enqueueProjectFilePut(
  projectId: string,
  path: string,
  content: string,
  expectedVersion: number,
  expectedOwner?: string | null,
): Promise<void> {
  const ownerId = currentOwner(expectedOwner);
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);

  const ops = await idbAll(store);
  for (const op of ops) {
    if (
      op.ownerId === ownerId &&
      op.originId === ORIGIN_ID &&
      !op.blocked &&
      !op.inFlight &&
      op.type === 'project-file-put' &&
      op.projectId === projectId &&
      op.path === path &&
      op.id !== undefined
    ) {
      await idbDelete(store, op.id);
    }
  }

  await idbAdd(
    store,
    {
      type: 'project-file-put',
      originId: ORIGIN_ID,
      projectId,
      path,
      content,
      expectedVersion,
      queuedAt: new Date().toISOString(),
    },
    ownerId,
  );
  db.close();
  notifyQueued();
}

/**
 * Number of pending operations (for the "pending sync" badge).
 * Rejects when IndexedDB is unavailable so the UI can report uncertainty.
 */
export async function getPendingCount(): Promise<number> {
  const owner = auth.getUserId();
  if (!owner) return 0;
  const db = await openDb();
  try {
    const ops = await idbAll(db.transaction(STORE, 'readonly').objectStore(STORE));
    return auth.getUserId() === owner ? ops.filter((op) => op.ownerId === owner).length : 0;
  } finally {
    db.close();
  }
}

/** Capture the precise queued snapshots superseded by an explicit online save. */
export async function getProjectFileQueueIds(
  projectId: string,
  path: string,
  content: string,
  resolveConflicts = false,
): Promise<number[]> {
  const ownerId = currentOwner();
  const db = await openDb();
  try {
    const ops = await idbAll(db.transaction(STORE, 'readonly').objectStore(STORE));
    if (auth.getUserId() !== ownerId) return [];
    return ops
      .filter(
        (op) =>
          op.ownerId === ownerId &&
          op.type === 'project-file-put' &&
          op.projectId === projectId &&
          op.path === path &&
          (resolveConflicts || op.content === content) &&
          op.id !== undefined,
      )
      .map((op) => op.id!);
  } finally {
    db.close();
  }
}

export async function clearProjectFileQueueIds(
  projectId: string,
  path: string,
  ids: number[],
): Promise<void> {
  const ownerId = currentOwner();
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const committed = transactionComplete(tx);
    const store = tx.objectStore(STORE);
    const ops = await idbAll(store);
    for (const op of ops) {
      if (
        op.ownerId === ownerId &&
        op.type === 'project-file-put' &&
        op.projectId === projectId &&
        op.path === path &&
        op.id !== undefined &&
        ids.includes(op.id)
      )
        await idbDelete(store, op.id);
    }
    await committed;
  } finally {
    db.close();
  }
  notifyQueued();
}

/** Rejected snapshots stay available only to their owning account. */
export async function getBlockedOperations(): Promise<QueuedOp[]> {
  const ownerId = auth.getUserId();
  if (!ownerId) return [];
  const db = await openDb();
  try {
    const ops = await idbAll(db.transaction(STORE, 'readonly').objectStore(STORE));
    return auth.getUserId() === ownerId
      ? ops.filter((op) => op.ownerId === ownerId && op.blocked)
      : [];
  } finally {
    db.close();
  }
}

/** Old records have no trustworthy owner and remain quarantined. */
export async function getLegacyOperationCount(): Promise<number> {
  const db = await openDb();
  try {
    const ops = await idbAll(db.transaction(STORE, 'readonly').objectStore(STORE));
    return ops.filter((op) => !op.ownerId).length;
  } finally {
    db.close();
  }
}

// ── Drain ─────────────────────────────────────────────────────────────────────

/**
 * Replays all queued ops in insertion order against the injected handlers.
 *  - success        → op removed
 *  - project conflict → retained and blocked for recovery
 *  - auth / network / server error → retained, stop replay
 *  - other rejected non-project 4xx → dropped
 * Returns the number of ops applied successfully. Always dispatches
 * `vault:queue-drained` at the end.
 */
async function markInFlight(
  id: number | undefined,
  ownerId: string,
  inFlight: boolean,
): Promise<boolean> {
  if (id === undefined) return false;
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const committed = transactionComplete(tx);
    const store = tx.objectStore(STORE);
    const changed = await new Promise<boolean>((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const current = request.result as QueuedOp | undefined;
        if (!current || current.ownerId !== ownerId || current.blocked) {
          resolve(false);
          return;
        }
        const update = store.put({ ...current, inFlight });
        update.onsuccess = () => resolve(true);
        update.onerror = () => reject(update.error);
      };
      request.onerror = () => reject(request.error);
    });
    await committed;
    return changed;
  } finally {
    db.close();
  }
}

const activeDrains = new Set<string>();

export async function processQueue(
  handlers: QueueHandlers,
  expectedOwner?: string,
): Promise<number> {
  const ownerId = auth.getUserId();
  if (!ownerId || (expectedOwner && ownerId !== expectedOwner) || activeDrains.has(ownerId))
    return 0;
  activeDrains.add(ownerId);
  try {
    if (navigator.locks) {
      return await navigator.locks.request(
        `vault-offline-queue:${ownerId}`,
        { ifAvailable: true },
        (lock) => (lock ? processOwnedQueue(handlers, ownerId) : 0),
      );
    }
    return await processOwnedQueue(handlers, ownerId);
  } finally {
    activeDrains.delete(ownerId);
  }
}

async function processOwnedQueue(handlers: QueueHandlers, expectedOwner: string): Promise<number> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    return 0;
  }

  const ownerId = auth.getUserId();
  if (!ownerId || (expectedOwner && ownerId !== expectedOwner)) {
    db.close();
    return 0;
  }
  const tx = db.transaction(STORE, 'readonly');
  const ops = (await idbAll(tx.objectStore(STORE))).filter(
    (op) => op.ownerId === ownerId && !op.blocked,
  );
  db.close();

  let succeeded = 0;

  for (const op of ops) {
    if (auth.getUserId() !== ownerId) break;
    if (!(await markInFlight(op.id, ownerId, true))) continue;
    const removeOp = async () => {
      const db2 = await openDb();
      const tx2 = db2.transaction(STORE, 'readwrite');
      const committed = transactionComplete(tx2);
      if (op.id !== undefined) await idbDelete(tx2.objectStore(STORE), op.id);
      await committed;
      db2.close();
    };

    try {
      if (op.type === 'course-create') {
        const saved = await handlers.courseCreate(op.data, op.tempId);
        if (saved && auth.getUserId() === ownerId) {
          // Swap temp id -> real highlight in the UI
          window.dispatchEvent(
            new CustomEvent('vault:hl-confirmed', {
              detail: { tempId: op.tempId, highlight: saved },
            }),
          );
        }
      } else if (op.type === 'course-delete') {
        await handlers.courseDelete(op.hlId);
      } else if (op.type === 'course-update') {
        await handlers.courseUpdate(op.hlId, op.patch);
      } else if (op.type === 'podcast-replace') {
        if (op.thoughts) await handlers.podcastReplace(op.podcastId, op.highlights, op.thoughts);
        else await handlers.podcastReplace(op.podcastId, op.highlights);
      } else if (op.type === 'project-file-put') {
        await handlers.projectFilePut({
          projectId: op.projectId,
          path: op.path,
          content: op.content,
          expectedVersion: op.expectedVersion,
        });
      }

      await removeOp();
      if (op.type === 'project-file-put' && auth.getUserId() === ownerId) {
        clearDraft('project', `${op.projectId}/${op.path}`, {
          content: op.content,
          version: op.expectedVersion,
        });
        window.dispatchEvent(
          new CustomEvent('vault:project-synced', {
            detail: { projectId: op.projectId, path: op.path, content: op.content, ownerId },
          }),
        );
      }
      succeeded += 1;
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (
        status === 409 ||
        (op.type === 'project-file-put' &&
          typeof status === 'number' &&
          status >= 400 &&
          status < 500 &&
          status !== 401 &&
          status !== 403)
      ) {
        const db2 = await openDb();
        const blockTx = db2.transaction(STORE, 'readwrite');
        const committed = transactionComplete(blockTx);
        const store = blockTx.objectStore(STORE);
        await new Promise<void>((resolve, reject) => {
          const request = store.get(op.id!);
          request.onsuccess = () => {
            // A newer snapshot may already have replaced this one.
            if (!request.result) {
              resolve();
              return;
            }
            const put = store.put({ ...request.result, blocked: true, inFlight: false });
            put.onsuccess = () => resolve();
            put.onerror = () => reject(put.error);
          };
          request.onerror = () => reject(request.error);
        });
        await committed;
        db2.close();
        if (op.type === 'project-file-put' && status === 409 && auth.getUserId() === ownerId) {
          window.dispatchEvent(
            new CustomEvent('vault:project-conflict', {
              detail: { projectId: op.projectId, path: op.path },
            }),
          );
        }
      } else if (status === 401 || status === 403) {
        await markInFlight(op.id, ownerId, false);
        break;
      } else if (
        typeof status === 'number' &&
        status >= 400 &&
        status < 500 &&
        op.type !== 'project-file-put'
      ) {
        await removeOp();
      } else {
        // Preserve content on all project errors and transient failures.
        await markInFlight(op.id, ownerId, false);
        break;
      }
    }
  }

  window.dispatchEvent(new CustomEvent('vault:queue-drained'));
  return succeeded;
}
